import { useCallback, useEffect, useMemo, useState } from "react";

/* Onde a ultima rota fica guardada entre sessoes. */
const ULTIMA_ROTA = "virtus:ultima-rota";
import { InstallPrompt } from "./components/InstallPrompt";
import { LiteMode } from "./components/LiteMode";
import { ModuleHome } from "./components/ModuleHome";
import { ModuleViewer } from "./components/ModuleViewer";
import { SearchPanel } from "./components/SearchPanel";
import { Sidebar } from "./components/Sidebar";
import { moduleSources } from "./data/moduleSources";
import type { ModuleId } from "./types";
import { buildSearchIndex, createModuleRecords } from "./utils/modules";
import { parseHashRoute, routeToHash, type LiteGroup } from "./utils/route";
import { searchModules } from "./utils/search";

function App() {
  // Records BASE (rápido, sem parsing de HTML) no primeiro paint. A busca é indexada
  // SOB DEMANDA quando o usuário abre a busca (não em background — evita competir por
  // banda com a navegação/carregamento dos módulos no mobile).
  const [modules, setModules] = useState(() => createModuleRecords(moduleSources));
  const [searchIndexed, setSearchIndexed] = useState(false);
  const [route, setRoute] = useState(() => {
    /* Reabrir onde parou. Quem fecha o app no meio de um modulo e volta dez minutos
       depois nao quer recomecar da capa. So vale quando NAO ha hash: um link
       compartilhado, ou o botao voltar, continuam mandando. */
    if (!window.location.hash || window.location.hash === "#") {
      try {
        const guardado = window.localStorage.getItem(ULTIMA_ROTA);
        if (guardado) {
          const r = parseHashRoute(guardado);
          if (r.view === "module" || r.view === "lite") return r;
        }
      } catch {
        /* armazenamento bloqueado (aba privada): abre na capa, sem drama */
      }
    }
    return parseHashRoute(window.location.hash);
  });
  const [query, setQuery] = useState("");

  /* Guarda a ultima rota util. A capa nao e guardada: voltar para ela e o padrao, e
     grava-la apagaria o modulo em que a pessoa estava. */
  useEffect(() => {
    if (route.view !== "module" && route.view !== "lite") return;
    try {
      window.localStorage.setItem(ULTIMA_ROTA, routeToHash(route));
    } catch {
      /* armazenamento bloqueado: seguir sem guardar */
    }
  }, [route]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  /* Com a gaveta aberta no celular, rolar arrastava o conteudo ATRAS dela, e o foco
     ficava no <body> — quem navega por teclado ou leitor de tela abria o sumario e o
     cursor nao ia junto. Trava a rolagem do fundo e leva o foco para dentro; ao
     fechar, devolve o foco a quem abriu. */
  useEffect(() => {
    if (typeof document === "undefined") return;
    const celular = window.innerWidth < 900;
    if (!isSidebarOpen || !celular) {
      document.body.classList.remove("app-shell--travado");
      return;
    }
    document.body.classList.add("app-shell--travado");
    const anterior = document.activeElement as HTMLElement | null;
    const gaveta = document.querySelector<HTMLElement>(".sidebar");
    const primeiro = gaveta?.querySelector<HTMLElement>(
      "button, a[href], [tabindex]:not([tabindex='-1'])"
    );
    primeiro?.focus();
    return () => {
      document.body.classList.remove("app-shell--travado");
      if (anterior && document.contains(anterior)) anterior.focus();
    };
  }, [isSidebarOpen]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [expandedModuleId, setExpandedModuleId] = useState<ModuleId | null>(() => route.moduleId);

  useEffect(() => {
    const onHashChange = () => setRoute(parseHashRoute(window.location.hash));
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // Índice de busca SOB DEMANDA: constrói uma única vez, quando o usuário abre a busca.
  // Carrega o HTML dos módulos + parseia (DOMParser) fora do startup — e só se a busca
  // for usada, sem baixar 1,65MB em background competindo com a navegação no mobile.
  useEffect(() => {
    if (!isSearchOpen || searchIndexed) {
      return;
    }
    let cancelled = false;
    buildSearchIndex(moduleSources).then((enriched) => {
      if (!cancelled) {
        setModules(enriched);
        setSearchIndexed(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isSearchOpen, searchIndexed]);

  useEffect(() => {
    setExpandedModuleId(route.moduleId);
  }, [route.moduleId]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsSearchOpen(false);
        setIsSidebarOpen(false);
      }
      // Cmd/Ctrl+K abre a busca global de qualquer lugar
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setIsSearchOpen((current) => !current);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const activeModule = modules.find((module) => module.id === route.moduleId) ?? modules[0];
  const results = useMemo(() => searchModules(modules, query), [modules, query]);

  const navigateTo = useCallback((moduleId: ModuleId, sectionId?: string) => {
    window.location.hash = routeToHash({ view: "module", moduleId, sectionId });
  }, []);

  const navigateHome = useCallback(() => {
    window.location.hash = routeToHash({ view: "home", moduleId: "modulo-01" });
  }, []);

  const navigateLite = useCallback((group?: LiteGroup, calcId?: string) => {
    window.location.hash = routeToHash({
      view: "lite",
      moduleId: "calculadoras",
      liteGroup: group,
      calcId
    });
  }, []);

  const navigateToModule = useCallback(
    (moduleId: ModuleId) => navigateTo(moduleId),
    [navigateTo]
  );

  // No LITE, fechar a calculadora dentro do modulo volta ao indice — sem isso
  // o usuario cairia no modulo inteiro, que e justamente o que o LITE evita.
  useEffect(() => {
    if (route.view !== "lite" || !route.calcId || !route.liteGroup) return;
    const group = route.liteGroup;
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === "codex-calc-closed") navigateLite(group);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [route.view, route.calcId, route.liteGroup, navigateLite]);

  const selectModule = (moduleId: ModuleId) => {
    if (route.view === "module" && moduleId === activeModule.id) {
      setExpandedModuleId((current) => (current === moduleId ? null : moduleId));
      return;
    }

    setExpandedModuleId(moduleId);
    navigateTo(moduleId);
  };

  const openSidebar = () => {
    setIsSearchOpen(false);
    setIsSidebarOpen(true);
  };

  const setSearchPanelOpen = (open: boolean) => {
    setIsSearchOpen(open);
    if (open) {
      setIsSidebarOpen(false);
    }
  };

  const isHome = route.view === "home";
  const isLite = route.view === "lite";

  return (
    <div className={`app-shell ${isSidebarOpen ? "app-shell--sidebar-open" : ""}`}>
      {/* Fundo clicavel da gaveta. Sem ele so o Esc e o proprio botao fechavam — e
          tocar fora, que e o gesto que todo mundo tenta primeiro, nao fazia nada.
          So aparece no celular, onde a gaveta cobre o conteudo. */}
      {isSidebarOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Fechar sumario de modulos"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      <Sidebar
        modules={modules}
        activeModule={activeModule}
        activeSectionId={route.sectionId}
        expandedModuleId={expandedModuleId}
        isOpen={isSidebarOpen}
        isHomeActive={isHome}
        isLiteActive={isLite}
        onSelectLite={() => {
          navigateLite();
          setIsSidebarOpen(false);
        }}
        onCollapse={() => setIsSidebarOpen(false)}
        onSelectHome={() => {
          navigateHome();
          setIsSidebarOpen(false);
        }}
        onSelectModule={(moduleId) => {
          selectModule(moduleId);
          // Fecha sidebar apenas em mobile — desktop mantém aberto
          if (window.innerWidth < 900) setIsSidebarOpen(false);
        }}
        onSelectSection={(moduleId, sectionId) => {
          setExpandedModuleId(moduleId);
          navigateTo(moduleId, sectionId);
          // Fecha sidebar apenas em mobile — no desktop mantém aberta para
          // navegar entre capítulos sem precisar reabrir o sumário.
          if (window.innerWidth < 900) setIsSidebarOpen(false);
        }}
      />

      <div className="workspace">
        <button
          className="module-menu-trigger"
          type="button"
          onClick={openSidebar}
          aria-label="Abrir sumario de modulos"
          title="Sumário de módulos"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <circle cx="5" cy="6.5" r="1.2" fill="currentColor" />
            <line x1="9" y1="6.5" x2="19" y2="6.5" />
            <circle cx="5" cy="12" r="1.2" fill="currentColor" />
            <line x1="9" y1="12" x2="19" y2="12" />
            <circle cx="5" cy="17.5" r="1.2" fill="currentColor" />
            <line x1="9" y1="17.5" x2="19" y2="17.5" />
          </svg>
        </button>

        <main className="content-grid">
          <div className="primary-column">
            {isLite ? (
              <LiteMode
                modules={modules}
                group={route.liteGroup}
                calcId={route.calcId}
                onSelectGroup={(group) => navigateLite(group)}
                onSelectCalc={(group, calcId) => navigateLite(group, calcId)}
                onBack={() => navigateLite(route.calcId ? route.liteGroup : undefined)}
                onExitLite={navigateHome}
                onNavigateModule={navigateToModule}
              />
            ) : isHome ? (
              <ModuleHome modules={modules} onSelectModule={navigateToModule} />
            ) : (
              <ModuleViewer
                module={activeModule}
                targetSectionId={route.sectionId}
                modules={modules}
                onNavigateModule={navigateToModule}
              />
            )}
          </div>
        </main>

        <SearchPanel
          query={query}
          results={results}
          isOpen={isSearchOpen}
          indexing={isSearchOpen && !searchIndexed}
          onOpenChange={setSearchPanelOpen}
          onQueryChange={setQuery}
          onSelectResult={(moduleId, sectionId) => {
            navigateTo(moduleId, sectionId);
            setIsSearchOpen(false);
            setIsSidebarOpen(false);
          }}
        />

        <nav className="mobile-action-bar" aria-label="Acoes do modulo">
          <button
            className="mobile-action-bar__btn"
            type="button"
            onClick={openSidebar}
            aria-label="Abrir sumario de modulos"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <circle cx="5" cy="6.5" r="1.2" fill="currentColor" />
              <line x1="9" y1="6.5" x2="19" y2="6.5" />
              <circle cx="5" cy="12" r="1.2" fill="currentColor" />
              <line x1="9" y1="12" x2="19" y2="12" />
              <circle cx="5" cy="17.5" r="1.2" fill="currentColor" />
              <line x1="9" y1="17.5" x2="19" y2="17.5" />
            </svg>
          </button>
          <button
            className={`mobile-action-bar__btn mobile-action-bar__btn--lite${isLite ? " mobile-action-bar__btn--on" : ""}`}
            type="button"
            onClick={() => navigateLite()}
            aria-label="Abrir modo LITE (calculadoras)"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" fill="currentColor" stroke="none" />
            </svg>
          </button>
          <button
            className="mobile-action-bar__btn"
            type="button"
            onClick={() => setSearchPanelOpen(true)}
            aria-label="Abrir busca global"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="7" />
              <path d="m16.5 16.5 4 4" />
            </svg>
          </button>
        </nav>
      </div>
      <InstallPrompt suppressed={isHome || isLite} />
    </div>
  );
}

export default App;
